import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { animate, set, stagger, createTimeline } from "animejs";
import countryList from "react-select-country-list";
import ReactCountryFlag from "react-country-flag";
import "react-phone-input-2/lib/style.css";
import "./Css/home.css";
import {
  getDemoTabs,
  getModules,
  getUseCases,
  getFaqs,
  getGpsBrands,
  sectionsOrder,
  translations,
} from "../utils/homeContent";
import Header from "./HomeSections/Header";
import Hero from "./HomeSections/Hero";
import TrustBar from "./HomeSections/TrustBar";
import ModulesSection from "./HomeSections/ModulesSection";
import HowItWorksSection from "./HomeSections/HowItWorksSection";
import UseCasesSection from "./HomeSections/UseCasesSection";
import ProductActionSection from "./HomeSections/ProductActionSection";
import EvidenceSection from "./HomeSections/EvidenceSection";
import IntegrationsSection from "./HomeSections/IntegrationsSection";
import WorldSection from "./HomeSections/WorldSection";
import SecuritySection from "./HomeSections/SecuritySection";
import PricingSection from "./HomeSections/PricingSection";
import ClientsSection from "./HomeSections/ClientsSection";
import FaqSection from "./HomeSections/FaqSection";
import FinalCTASection from "./HomeSections/FinalCTASection";
import SiteFooter from "./HomeSections/SiteFooter";
import FloatingNav from "./HomeSections/FloatingNav";
import GDPR from "./GDPR/GDPR";
import TermsConditions from "./TermsConditions/TermsConditions";
import PrivacyPolician from "./PrivacyPolician/PrivacyPolician";
import { useAuth } from "../auth/AuthContext";

const HomePage = () => {
  const [activeDemo, setActiveDemo] = useState("mapa");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [language, setLanguage] = useState("es");
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const animatedSectionsRef = useRef(new Set());
  const globeWrapRef = useRef(null);
  const globeStageRef = useRef(null);
  const brandOrbitRef = useRef(null);
  const brandItemRefs = useRef([]);
  const loginMenuRef = useRef(null);
  const loginMenuMobileRef = useRef(null);
  const langMenuRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useLayoutEffect(() => {
    document.body.classList.add("home-page");
    document.documentElement.classList.add("home-page");
    const onEsc = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
        setIsLoginOpen(false);
      }
    };
    window.addEventListener("keydown", onEsc);
    const onClickOutside = (event) => {
      const desktop = loginMenuRef.current;
      const mobile = loginMenuMobileRef.current;
      const langMenu = langMenuRef.current;
      if (desktop && desktop.contains(event.target)) return;
      if (mobile && mobile.contains(event.target)) return;
      if (langMenu && langMenu.contains(event.target)) return;
      setIsLoginOpen(false);
      setIsLangOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return () => {
      document.body.classList.remove("home-page");
      document.documentElement.classList.remove("home-page");
      document.documentElement.style.removeProperty("--home-header-offset");
      window.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (showSplash) return undefined;
    const updateHeaderOffset = () => {
      const header = document.querySelector(".home-header");
      const height = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      const offset = height;
      document.documentElement.style.setProperty("--home-header-offset", `${offset}px`);
    };
    updateHeaderOffset();
    window.addEventListener("resize", updateHeaderOffset);
    let resizeObserver;
    const header = document.querySelector(".home-header");
    if (header && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateHeaderOffset);
      resizeObserver.observe(header);
    }
    return () => {
      window.removeEventListener("resize", updateHeaderOffset);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [showSplash]);

  useEffect(() => {
    const minDuration = 2400; // ms de permanencia visible
    const start = performance.now();
    const scheduleHide = () => {
      const elapsed = performance.now() - start;
      const wait = Math.max(0, minDuration - elapsed);
      setTimeout(() => setShowSplash(false), wait);
    };
    window.addEventListener("load", scheduleHide, { once: true });
    const fallback = setTimeout(scheduleHide, minDuration + 800);
    return () => {
      window.removeEventListener("load", scheduleHide);
      clearTimeout(fallback);
    };
  }, []);

  const formatValue = (value, target, decimals) => {
    const absTarget = Math.abs(target);
    if (absTarget >= 1000000) {
      const v = (value / 1000000).toFixed(1).replace(/\.0$/, "");
      return `${v}M`;
    }
    if (absTarget >= 1000) {
      return `${Math.round(value / 1000)}K`;
    }
    if (decimals > 0) {
      return value.toFixed(decimals);
    }
    return `${Math.round(value)}`;
  };

  useEffect(() => {
    if (showSplash) return;
    const root = document.querySelector(".home");
    if (!root) return;

    const allAnimTargets = Array.from(
      root.querySelectorAll(".anim-section, .anim-card, .anim-visual")
    ).filter((el) => !el.closest("#hero"));
    if (allAnimTargets.length) {
      set(allAnimTargets, { opacity: 0, translateY: 24 });
    }

    const animateSection = (section) => {
      if (!section || animatedSectionsRef.current.has(section)) return;
      animatedSectionsRef.current.add(section);

      if (section.id === "hero") {
        const counters = section.querySelectorAll("[data-count-target]");
        counters.forEach((el) => {
          const target = Number(el.getAttribute("data-count-target") || 0);
          const decimals = Number(el.getAttribute("data-count-decimals") || 0);
          const prefix = el.getAttribute("data-count-prefix") || "";
          const suffix = el.getAttribute("data-count-suffix") || "";
          const counter = { val: 0 };
          animate(counter, {
            val: target,
            duration: 1200,
            easing: "easeOutCubic",
            onUpdate: () => {
              el.textContent = `${prefix}${formatValue(
                counter.val,
                target,
                decimals
              )}${suffix}`;
            },
          });
        });

        const bars = section.querySelectorAll("[data-progress]");
        bars.forEach((el) => {
          const progress = Number(el.getAttribute("data-progress") || 0);
          el.style.width = "0%";
          animate(el, {
            width: `${Math.max(0, Math.min(100, progress))}%`,
            duration: 900,
            easing: "easeOutCubic",
            delay: 200,
          });
        });
        return;
      }

      const visualItems = section.querySelectorAll(".anim-visual");
      const sections = [section, ...section.querySelectorAll(".anim-section")];
      const cards = section.querySelectorAll(".anim-card");

      createTimeline({ easing: "easeOutExpo", duration: 900 })
        .add(
          visualItems,
          {
            opacity: [0, 1],
            translateY: [24, 0],
          },
          0
        )
        .add(
          sections,
          {
            opacity: [0, 1],
            translateY: [24, 0],
            delay: stagger(120),
          },
          "-=500"
        )
        .add(
          cards,
          {
            opacity: [0, 1],
            translateY: [24, 0],
            delay: stagger(60),
          },
          "-=650"
        );

      const counters = section.querySelectorAll("[data-count-target]");
      counters.forEach((el) => {
        const target = Number(el.getAttribute("data-count-target") || 0);
        const decimals = Number(el.getAttribute("data-count-decimals") || 0);
        const prefix = el.getAttribute("data-count-prefix") || "";
        const suffix = el.getAttribute("data-count-suffix") || "";
        const counter = { val: 0 };
        animate(counter, {
          val: target,
          duration: 1200,
          easing: "easeOutCubic",
          onUpdate: () => {
            el.textContent = `${prefix}${formatValue(
              counter.val,
              target,
              decimals
            )}${suffix}`;
          },
        });
      });

      const bars = section.querySelectorAll("[data-progress]");
      bars.forEach((el) => {
        const progress = Number(el.getAttribute("data-progress") || 0);
        el.style.width = "0%";
        animate(el, {
          width: `${Math.max(0, Math.min(100, progress))}%`,
          duration: 900,
          easing: "easeOutCubic",
          delay: 200,
        });
      });

      if (section.id === "mundo-gps") {
        const globe = section.querySelector(".anim-world");
        const brandCards = section.querySelectorAll(".anim-brand");

        if (globe) {
          set(globe, { opacity: 0, scale: 0.94, translateY: 24, rotateX: 14 });
          createTimeline({ easing: "easeOutExpo", duration: 1200 })
            .add(globe, {
              opacity: [0, 1],
              scale: [0.94, 1.02],
              translateY: [24, 0],
              rotateX: [14, 6],
            })
            .add(
              globe,
              {
                scale: [1.02, 1],
                rotateX: [6, 2],
                duration: 900,
              },
              "-=450"
            );
        }

        if (brandCards.length) {
          set(brandCards, { opacity: 0, translateY: 12, scale: 0.98 });
          animate(brandCards, {
            opacity: [0, 1],
            translateY: [12, 0],
            scale: [0.98, 1],
            duration: 520,
            easing: "easeOutCubic",
            delay: stagger(90, { start: 350 }),
          });
        }

        const copy = section.querySelector(".anim-world-copy");
        if (copy) {
          set(copy, { opacity: 0, translateY: 16 });
          animate(copy, {
            opacity: [0, 1],
            translateY: [16, 0],
            duration: 700,
            easing: "easeOutCubic",
            delay: 420,
          });
        }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateSection(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -15% 0px" }
    );

    const sections = root.querySelectorAll(".home-section");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [showSplash]);

  useEffect(() => {
    if (showSplash) return undefined;
    animate(".tab-panel .tab-copy, .tab-panel .panel-preview", {
      opacity: [0, 1],
      translateY: [8, 0],
      duration: 350,
      easing: "easeOutQuad",
    });
  }, [activeDemo]);

  useEffect(() => {
    if (showSplash) return undefined;
    const stage = globeStageRef.current;
    const wrapper = globeWrapRef.current;
    const orbit = brandOrbitRef.current;
    if (!stage || !wrapper || !orbit) return undefined;

    const brandEls = brandItemRefs.current.filter(Boolean);
    if (!brandEls.length) return undefined;

    let frameId;
    let hover = false;
    let tiltX = 0;
    let tiltY = 0;
    let targetTiltX = 0;
    let targetTiltY = 0;
    let zoom = 1.02;
    let targetZoom = 1.02;
    let lastWidth = 0;
    let lastHeight = 0;
    let radius = 0;
    let orbitSpeed = 0.00006;
    let paused = false;
    let phase = 0;
    let lastTime = null;
    const baseAngles = brandEls.map((_, idx) => (idx / brandEls.length) * Math.PI * 2);
    const zDepth = 0.35;

    const updateRadius = () => {
      const { width, height } = wrapper.getBoundingClientRect();
      if (!width || !height) return;
      if (width === lastWidth && height === lastHeight) return;
      lastWidth = width;
      lastHeight = height;
      radius = Math.min(width, height) * 0.42;
      orbit.style.width = `${width}px`;
      orbit.style.height = `${height}px`;
    };

    const onMove = () => {
      targetZoom = 1.03;
    };

    const onEnter = () => {
      hover = true;
      targetZoom = 1.04;
    };

    const onLeave = () => {
      hover = false;
      targetTiltX = 0;
      targetTiltY = 0;
      targetZoom = 1.02;
    };

    const onBrandEnter = (event) => {
      paused = true;
      event.currentTarget.classList.add("is-active");
    };
    const onBrandLeave = (event) => {
      paused = false;
      event.currentTarget.classList.remove("is-active");
    };

    brandEls.forEach((el) => {
      el.addEventListener("mouseenter", onBrandEnter);
      el.addEventListener("mouseleave", onBrandLeave);
    });

    wrapper.addEventListener("mousemove", onMove);
    wrapper.addEventListener("mouseenter", onEnter);
    wrapper.addEventListener("mouseleave", onLeave);

    const tick = (time) => {
      updateRadius();

      tiltX += (targetTiltX - tiltX) * 0.08;
      tiltY += (targetTiltY - tiltY) * 0.08;
      zoom += (targetZoom - zoom) * 0.08;

      stage.style.transform = `perspective(900px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${zoom})`;

      if (lastTime === null) lastTime = time;
      const delta = time - lastTime;
      lastTime = time;
      if (!paused) {
        phase += delta * orbitSpeed;
      }
      const t = phase;
      brandEls.forEach((el, idx) => {
        const angle = baseAngles[idx] + t;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius * 0.6;
        const z = Math.sin(angle) * zDepth;
        const scale = 0.85 + (z + 1) * 0.12;
        const opacity = 0.5 + (z + 1) * 0.25;
        el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${scale})`;
        el.style.opacity = opacity;
      });

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      wrapper.removeEventListener("mousemove", onMove);
      wrapper.removeEventListener("mouseenter", onEnter);
      wrapper.removeEventListener("mouseleave", onLeave);
      brandEls.forEach((el) => {
        el.removeEventListener("mouseenter", onBrandEnter);
        el.removeEventListener("mouseleave", onBrandLeave);
      });
      stage.style.transform = "";
    };
  }, [showSplash]);
  const demoTabs = useMemo(() => getDemoTabs(language), [language]);
  const modules = useMemo(() => getModules(language), [language]);
  const useCases = useMemo(() => getUseCases(language), [language]);
  const faqs = useMemo(() => getFaqs(language), [language]);
  const gpsBrands = useMemo(() => getGpsBrands(language), [language]);
  const seo = useMemo(
    () => ({
      title:
        language === "es"
          ? "Cargotronics | Plataforma de Seguridad y Tracking de Carga"
          : "Cargotronics | Cargo Security & Real-Time Tracking Platform",
      description:
        language === "es"
          ? "Plataforma de seguridad de carga con tracking en tiempo real, video telemática, alertas y evidencia en campo para logística y transporte."
          : "Cargo security platform with real-time tracking, video telematics, alerts, and field evidence for logistics and transport operations.",
      keywords:
        language === "es"
          ? "seguridad de carga, tracking, telemática, logística, monitoreo de flotas, evidencia en campo, alertas, cargotronics"
          : "cargo security, tracking, telematics, logistics, fleet monitoring, field evidence, alerts, cargotronics",
      imageAlt:
        language === "es"
          ? "Cargotronics plataforma de seguridad de carga"
          : "Cargotronics cargo security platform",
      locale: language === "es" ? "es_CO" : "en_US",
      imagePath: "/WebInicio.png",
    }),
    [language]
  );

  useEffect(() => {
    const upsertMeta = (key, keyValue, content) => {
      if (!content) return;
      let tag = document.querySelector(`meta[${key}="${keyValue}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(key, keyValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    const upsertLink = (rel, href) => {
      if (!href) return;
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", rel);
        document.head.appendChild(link);
      }
      link.setAttribute("href", href);
    };

    const origin = window.location.origin;
    const url = `${origin}/`;
    const imageUrl = `${origin}${seo.imagePath}`;

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta(
      "name",
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "Cargotronics");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", seo.imageAlt);
    upsertMeta("property", "og:locale", seo.locale);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertLink("canonical", url);

    const ldJson = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Cargotronics",
        url,
        logo: `${origin}/Cargotronics.png`,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Cargotronics",
        url,
        potentialAction: {
          "@type": "SearchAction",
          target: `${url}?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ];
    let ldScript = document.getElementById("ld-json-home");
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.id = "ld-json-home";
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify(ldJson);
  }, [seo]);

  const activeTab = demoTabs.find((tab) => tab.id === activeDemo);
  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const scroller = document.scrollingElement || document.documentElement;
    const header = document.querySelector(".home-header");
    const headerOffset = header ? Math.ceil(header.getBoundingClientRect().height) + 8 : 0;
    const targetTop = target.getBoundingClientRect().top + scroller.scrollTop - headerOffset;
    scroller.scrollTo({ top: targetTop, behavior: "smooth" });
  };
  const t = translations[language];
  const [selectedCountry, setSelectedCountry] = useState(null);
  const countryOptions = useMemo(() => countryList().getData(), []);
  const countrySelectOptions = useMemo(
    () => countryOptions.map((country) => ({ ...country })),
    [countryOptions]
  );
  const [phoneValue, setPhoneValue] = useState("");

  const formatCountryOption = (option) => (
    <div className="country-option">
      <ReactCountryFlag
        className="country-flag"
        countryCode={option.value}
        svg
        aria-label={option.label}
      />
      <span className="country-name">{option.label}</span>
    </div>
  );
  const getCurrentSection = () => {
    const scroller = document.scrollingElement || document.documentElement;
    const scrollY = scroller.scrollTop;
    const headerOffsetRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--home-header-offset")
      .trim();
    const headerOffset = Number.parseInt(headerOffsetRaw, 10) || 0;
    for (let i = sectionsOrder.length - 1; i >= 0; i -= 1) {
      const section = document.getElementById(sectionsOrder[i]);
      if (section && scrollY + headerOffset >= section.offsetTop) {
        return sectionsOrder[i];
      }
    }
    return sectionsOrder[0];
  };
  const handlePrevSection = () => {
    const current = getCurrentSection();
    const idx = sectionsOrder.indexOf(current);
    const prev = sectionsOrder[Math.max(0, idx - 1)];
    scrollToSection(prev);
  };
  const handleNextSection = () => {
    const current = getCurrentSection();
    const idx = sectionsOrder.indexOf(current);
    const next = sectionsOrder[Math.min(sectionsOrder.length - 1, idx + 1)];
    scrollToSection(next);
  };

  const handleLoginSelect = (tenant) => {
    console.log("Selected tenant:", tenant);
    if (tenant === "logiseguridad") {
      window.location.href = `/login`;
      setIsLoginOpen(false);
    }

    if (tenant === "cargotronics") {
      window.location.href = `https://infocarga-frontend-jwt-2-6t5m.vercel.app/login`;
      setIsLoginOpen(false);
    }
  };

  const handleDashboard = () => {
    setIsLoginOpen(false);
    setIsMenuOpen(false);
    navigate("/principal");
  };
  useEffect(() => {
    console.log("Current language:", language);
    if (language) {
      localStorage.setItem("preferredLanguage", language);
    }
  }, [language]);

  if (showSplash) {
    return (
      <div className="splash-screen">
        <img
          className="splash-logo"
          src={`${process.env.PUBLIC_URL}/Cargotronics.png`}
          alt="Cargotronics"
        />
      </div>
    );
  }


  return (
    <div className="home">
      <Header
        t={t}
        language={language}
        setLanguage={setLanguage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        isLangOpen={isLangOpen}
        setIsLangOpen={setIsLangOpen}
        scrollToSection={scrollToSection}
        handleLoginSelect={handleLoginSelect}
        isAuthenticated={isAuthenticated}
        handleDashboard={handleDashboard}
        loginMenuRef={loginMenuRef}
        loginMenuMobileRef={loginMenuMobileRef}
        langMenuRef={langMenuRef}
      />

      <Hero t={t} language={language} scrollToSection={scrollToSection} />
      <TrustBar t={t} />
      <IntegrationsSection t={t} language={language} />
      <UseCasesSection useCases={useCases} t={t} />
      <ProductActionSection
        t={t}
        demoTabs={demoTabs}
        activeDemo={activeDemo}
        setActiveDemo={setActiveDemo}
        activeTab={activeTab}
      />
      <EvidenceSection language={language} />
      <WorldSection
        t={t}
        language={language}
        gpsBrands={gpsBrands}
        globeStageRef={globeStageRef}
        globeWrapRef={globeWrapRef}
      />
      <ClientsSection gpsBrands={gpsBrands} language={language} />
      <FaqSection faqs={faqs} t={t} />
      <FinalCTASection
        t={t}
        language={language}
        phoneValue={phoneValue}
        setPhoneValue={setPhoneValue}
        countrySelectOptions={countrySelectOptions}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        formatCountryOption={formatCountryOption}
      />
      <SiteFooter
        t={t}
        language={language}
        setLanguage={setLanguage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        isLoginOpen={isLoginOpen}
        setIsLoginOpen={setIsLoginOpen}
        isLangOpen={isLangOpen}
        setIsLangOpen={setIsLangOpen}
        scrollToSection={scrollToSection}
        handleLoginSelect={handleLoginSelect}
        loginMenuRef={loginMenuRef}
        loginMenuMobileRef={loginMenuMobileRef}
        langMenuRef={langMenuRef} />
    </div>
  );
};

const Home = () => {
  const location = useLocation();
  if (location.pathname === "/gdpr") {
    return <GDPR />;
  }
  if (location.pathname === "/terminos-condiciones") {
    return <TermsConditions />;
  }
  if (location.pathname === "/politicas-privacidad") {
    return <PrivacyPolician />;
  }
  return <HomePage />;
};

export default Home;
